import { NavRoute } from './types';
import { usePointTasksContext } from '../../../../helpers/hooks/pointTasksContext';
import { getTaskMaxPoints } from '../../../../helpers/utils/rewardLabels';

const ChevronRight = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polyline points='9 18 15 12 9 6' />
  </svg>
);

const WalletIcon = () => (
  <svg
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1' />
    <path d='M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4' />
  </svg>
);

const StarIcon = () => (
  <svg
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' />
  </svg>
);

const StreakIcon = () => (
  <svg
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
  </svg>
);

export const OnboardingMetrics = ({ onNav }: { onNav: (route: NavRoute) => void }) => {
  const { getTasksByCode } = usePointTasksContext();
  const firstPaymentTask = getTasksByCode(['firstPayment'])[0];
  const firstPaymentPoints = getTaskMaxPoints(firstPaymentTask);

  return (
    <div className='ob-metrics'>
      <div className='ob-card ob-card-gold'>
        <div className='ob-card-grid' aria-hidden='true' />
        <div className='ob-card-glow ob-card-glow-gold' aria-hidden='true' />

        <div className='ob-card-header'>
          <span className='ob-badge ob-badge-category-gold'>First payment</span>
          <span className='ob-badge ob-badge-status'>Not set up</span>
        </div>

        <div className='ob-card-main'>
          <div className='ob-card-icon-wrap ob-card-icon-wrap-gold'>
            <div className='ob-card-icon ob-card-icon-gold'>
              <WalletIcon />
            </div>
          </div>
          <div className='ob-card-copy'>
            <h3 className='ob-card-title'>Pay your first rent</h3>
            <p className='ob-card-desc'>Add your rental to see your amount &amp; due date.</p>
          </div>
        </div>

        <div className='ob-card-placeholder'>
          <span className='ob-card-placeholder-icon'>$</span>
          Win your first rent payment
        </div>

        <button
          type='button'
          className='ob-card-action ob-card-action-gold'
          onClick={() => onNav('pay-rent')}
        >
          <span aria-hidden='true'>+</span>
          Pay your first rent
        </button>
        <p className='ob-card-footnote'>Takes under a minute · No fees to start</p>
      </div>

      <div className='ob-card ob-card-blue'>
        <div className='ob-card-grid' aria-hidden='true' />
        <div className='ob-card-glow ob-card-glow-blue' aria-hidden='true' />

        <div className='ob-card-header'>
          <span className='ob-badge ob-badge-category-blue'>Points balance</span>
          <span className='ob-badge ob-badge-status'>Not started</span>
        </div>

        <div className='ob-card-main'>
          <div className='ob-card-icon-wrap ob-card-icon-wrap-blue'>
            <div className='ob-card-icon ob-card-icon-blue'>
              <StarIcon />
            </div>
          </div>
          <div className='ob-card-copy'>
            <h3 className='ob-card-title'>Earn your first points</h3>
            <p className='ob-card-desc'>Points land after your first rent payment</p>
          </div>
        </div>

        {firstPaymentPoints > 0 ? (
          <div className='ob-card-status ob-card-status-blue'>
            <span className='ob-card-status-val mr-1'>+{firstPaymentPoints.toLocaleString()} </span>
            <span className='ob-card-status-sub'> points waiting on payment #1</span>
          </div>
        ) : null}

        <div className='ob-card-links'>
          <button
            type='button'
            className='ob-card-link ob-card-link-blue'
            onClick={() => onNav('rewards')}
          >
            Start earning
            <ChevronRight />
          </button>
          <button type='button' className='ob-card-link-muted' onClick={() => onNav('wallet')}>
            Redeem at 115+ brands
          </button>
        </div>
      </div>

      <div className='ob-card ob-card-green'>
        <div className='ob-card-grid' aria-hidden='true' />
        <div className='ob-card-glow ob-card-glow-green' aria-hidden='true' />

        <div className='ob-card-header'>
          <span className='ob-badge ob-badge-category-green'>Payment streak</span>
          <span className='ob-badge ob-badge-status'>Not started</span>
        </div>

        <div className='ob-card-main'>
          <div className='ob-card-icon-wrap ob-card-icon-wrap-green'>
            <div className='ob-card-icon ob-card-icon-green'>
              <StreakIcon />
            </div>
          </div>
          <div className='ob-card-copy'>
            <h3 className='ob-card-title'>Start your streak</h3>
            <p className='ob-card-desc'>Build credit every on-time month</p>
          </div>
        </div>

        <div className='ob-card-streak-block'>
          <div className='ob-streak-dots' aria-hidden='true'>
            {Array.from({ length: 6 }).map((_, index) => (
              <span key={index} />
            ))}
          </div>
          <p className='ob-streak-hint'>
            Pay on time to light up your <strong>6-month</strong> streak
          </p>
        </div>

        <div className='ob-card-links'>
          <button
            type='button'
            className='ob-card-link ob-card-link-green'
            onClick={() => onNav('pay-rent')}
          >
            Make first payment
            <ChevronRight />
          </button>
          <span className='ob-card-link-muted ob-card-link-static'>Reported to Equifax</span>
        </div>
      </div>
    </div>
  );
};
